import React, { useState, useEffect } from "react";
import {
  Box, Card, Container, Typography, Grid, Chip, Button, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Stack, IconButton, Tooltip, Alert, CircularProgress,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { collection, onSnapshot, query, orderBy, doc, updateDoc, addDoc, serverTimestamp, where } from "firebase/firestore";
import { db } from "../database/db";
import Page from "../components/Page";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function StreamCard({ stream, onEnd, onWarn }) {
  const duration = stream.startedAt
    ? Math.floor((Date.now() - (stream.startedAt.toDate ? stream.startedAt.toDate() : new Date(stream.startedAt)).getTime()) / 60000)
    : 0;

  return (
    <Card sx={{
      p: 2.5, borderRadius: 3,
      border: "1px solid rgba(61,193,14,0.3)",
      boxShadow: "0 0 20px rgba(61,193,14,0.1)",
      position: "relative",
      overflow: "visible",
    }}>
      {/* LIVE badge */}
      <Box sx={{
        position: "absolute", top: -8, right: 12,
        bgcolor: "#FF4842", color: "#fff", borderRadius: 1,
        px: 1, py: 0.25, fontSize: 10, fontWeight: 800, letterSpacing: 1,
      }}>
        ● LIVE
      </Box>

      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Avatar src={stream.hostPhoto} sx={{ width: 48, height: 48 }}>
          {(stream.hostName || "?").charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{stream.hostName || "Unknown Host"}</Typography>
          <Typography variant="caption" color="text.secondary">@{stream.hostUsername || "—"}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {stream.title || "No title"}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
            <Chip icon={<Icon icon="eva:people-fill" width={12} />}
              label={`${stream.viewerCount || 0} viewers`} size="small" color="primary" />
            <Chip icon={<Icon icon="eva:clock-fill" width={12} />}
              label={`${duration}m`} size="small" />
            {stream.topic && (
              <Chip label={`$${stream.topic}`} size="small" color="success" />
            )}
          </Stack>
        </Box>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button size="small" variant="outlined" color="warning" fullWidth
          startIcon={<Icon icon="eva:alert-triangle-fill" />}
          onClick={() => onWarn(stream)}>
          Warn
        </Button>
        <Button size="small" variant="outlined" color="error" fullWidth
          startIcon={<Icon icon="eva:slash-fill" />}
          onClick={() => onEnd(stream)}>
          End Stream
        </Button>
      </Stack>
    </Card>
  );
}

export default function StreamMonitor() {
  const [activeStreams, setActiveStreams] = useState([]);
  const [recentEnded, setRecentEnded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [endDialog, setEndDialog] = useState({ open: false, stream: null, reason: "" });
  const [warnDialog, setWarnDialog] = useState({ open: false, stream: null, message: "" });

  useEffect(() => {
    // Subscribe to active streams
    const activeQ = query(
      collection(db, "Streams"),
      where("status", "==", "live"),
      orderBy("startedAt", "desc")
    );
    const recentQ = query(
      collection(db, "Streams"),
      where("status", "==", "ended"),
      orderBy("endedAt", "desc")
    );

    const unsub1 = onSnapshot(activeQ, (snap) => {
      setActiveStreams(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));

    const unsub2 = onSnapshot(recentQ, (snap) => {
      setRecentEnded(snap.docs.slice(0, 10).map((d) => ({ id: d.id, ...d.data() })));
    }, () => {});

    return () => { unsub1(); unsub2(); };
  }, []);

  const handleEndStream = async () => {
    const { stream, reason } = endDialog;
    try {
      await updateDoc(doc(db, "Streams", stream.id), {
        status: "ended",
        endedAt: serverTimestamp(),
        endedBy: "admin",
        endReason: reason,
      });
      await addDoc(collection(db, "AuditLog"), {
        action: "stream-ended",
        targetName: stream.hostName || stream.hostUsername,
        details: { reason, title: stream.title },
        timestamp: serverTimestamp(),
      });
      // Notify host
      await addDoc(collection(db, "Notifications"), {
        userId: stream.hostId,
        title: "Stream Ended by Admin",
        body: reason || "Your stream was ended by an administrator.",
        type: "admin-action",
        createdAt: serverTimestamp(),
      });
      toast.success("Stream ended");
      setEndDialog({ open: false, stream: null, reason: "" });
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  const handleWarnHost = async () => {
    const { stream, message } = warnDialog;
    try {
      await addDoc(collection(db, "Notifications"), {
        userId: stream.hostId,
        title: "Admin Warning",
        body: message || "Your stream content has been flagged. Please review community guidelines.",
        type: "admin-warning",
        createdAt: serverTimestamp(),
      });
      await addDoc(collection(db, "AuditLog"), {
        action: "stream-warned",
        targetName: stream.hostName || stream.hostUsername,
        details: { message, title: stream.title },
        timestamp: serverTimestamp(),
      });
      toast.success("Warning sent to host");
      setWarnDialog({ open: false, stream: null, message: "" });
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  const totalViewers = activeStreams.reduce((s, st) => s + (st.viewerCount || 0), 0);

  return (
    <Page title="Stream Monitor | Fire Investing Admin">
      <ToastContainer position="top-right" />
      <Container maxWidth="xl">
        <Box sx={{ pb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Live Stream Monitor</Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor active streams in real-time — {activeStreams.length} live now · {totalViewers} total viewers
          </Typography>
        </Box>

        {activeStreams.length === 0 && !loading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No streams are currently live. Active streams will appear here automatically.
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
            <CircularProgress sx={{ color: "#3dc10e" }} />
          </Box>
        )}

        {/* Active streams */}
        {activeStreams.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: "#3dc10e" }}>
              🔴 {activeStreams.length} Active Streams
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {activeStreams.map((stream) => (
                <Grid item xs={12} sm={6} md={4} key={stream.id}>
                  <StreamCard stream={stream}
                    onEnd={(s) => setEndDialog({ open: true, stream: s, reason: "" })}
                    onWarn={(s) => setWarnDialog({ open: true, stream: s, message: "" })} />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Recent ended streams */}
        {recentEnded.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: "text.secondary" }}>
              Recent Streams (Ended)
            </Typography>
            <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
              {recentEnded.map((stream, i) => (
                <Box key={stream.id} sx={{
                  px: 2.5, py: 1.5,
                  borderBottom: i < recentEnded.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
                  display: "flex", alignItems: "center", gap: 2,
                }}>
                  <Avatar src={stream.hostPhoto} sx={{ width: 32, height: 32, fontSize: 12 }}>
                    {(stream.hostName || "?").charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{stream.hostName} — {stream.title || "Untitled"}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Peak: {stream.peakViewers || stream.viewerCount || 0} viewers ·{" "}
                      {stream.endedAt ? (stream.endedAt.toDate ? stream.endedAt.toDate() : new Date(stream.endedAt)).toLocaleString() : "—"}
                      {stream.endedBy === "admin" && " · Ended by admin"}
                    </Typography>
                  </Box>
                  <Chip label={stream.endReason || "normal"} size="small" color={stream.endedBy === "admin" ? "error" : "default"} />
                </Box>
              ))}
            </Card>
          </>
        )}
      </Container>

      {/* End Stream Dialog */}
      <Dialog open={endDialog.open} onClose={() => setEndDialog({ open: false, stream: null, reason: "" })} maxWidth="xs" fullWidth>
        <DialogTitle>End Stream</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Force-end <strong>{endDialog.stream?.hostName}</strong>'s stream "{endDialog.stream?.title}"?
            The host will receive a notification.
          </Typography>
          <TextField fullWidth label="Reason (sent to host)" value={endDialog.reason}
            onChange={(e) => setEndDialog((p) => ({ ...p, reason: e.target.value }))}
            placeholder="e.g. Community guidelines violation" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndDialog({ open: false, stream: null, reason: "" })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleEndStream}>End Stream</Button>
        </DialogActions>
      </Dialog>

      {/* Warn Dialog */}
      <Dialog open={warnDialog.open} onClose={() => setWarnDialog({ open: false, stream: null, message: "" })} maxWidth="xs" fullWidth>
        <DialogTitle>Warn Host</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Send an in-app warning to <strong>{warnDialog.stream?.hostName}</strong>. Stream stays live.
          </Typography>
          <TextField fullWidth multiline rows={3} label="Warning Message" value={warnDialog.message}
            onChange={(e) => setWarnDialog((p) => ({ ...p, message: e.target.value }))}
            placeholder="Your stream content violates our community guidelines..." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarnDialog({ open: false, stream: null, message: "" })}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleWarnHost}>Send Warning</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
